import Analytics from '@/utils/analytics';
import Animated from 'react-native-reanimated';
import MergeIcon from '@assets/icons/merged.svg';
import { ActivityIndicator } from '@/components/activity-indicator';
import { createStyles } from '@/contexts/theme-context';
import {
  type FC,
  type ReactNode,
  type RefObject,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  FONTS,
  LIST_MARGIN_HORIZONTAL,
  PAGE_CONTAINER_HEADER_HEIGHT,
} from '@/styles';
import { ICON_BUTTON_HIT_SLOP } from '@/components/navigation-header-content';
import { InboundListScreenHeader } from '@/screens/order-inbounds/inbound-list-screen-header';
import {
  Props as ListItemProps,
  SwipeableWithInstantClose,
} from '@/components/list-item';
import {
  OrderAgentWorkflowMode,
  OrderInboundOption,
  OrderInboundSection,
} from '@/screens/order-inbounds/types';
import {
  OrderInboundsList,
  useSharedOrderListStyles,
} from '@/screens/order-inbounds/order-inbounds-list';
import { OrderInboundsListItemSourceIcon } from '@/screens/order-inbounds/order-inbounds-list-item-source-icon';
import { OrderInboundsOrderListEmptyState } from './order-inbounds-order-list-empty-state';
import { OrderInboundsOrderListErrorState } from './order-inbounds-order-list-error-state';
import { OrderInboundsOrderListItem } from './order-inbounds-order-list-item';
import { OrderInboundsOrderListItemRightActions } from './order-inbounds-order-list-item-right-actions';
import { OrderInboundsOrderListItemSwipeableActions } from './order-inbounds-order-list-item-swipeable-actions';
import {
  OrdersByCustomerMap,
  skeletonData,
} from '@/screens/order-inbounds/utils';
import {
  Platform,
  type ScrollViewProps,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import { RecyclerListRef } from '@/components/recycler-list';
import { useAuthenticatedUserContext } from '@/contexts/user-context';
import { useHideOnScrollAnimation } from '@/screens/order-inbounds/hooks/use-hide-on-scroll-animation';
import { useIsSmallWebLayout } from '@/screens/order-inbounds/hooks/use-is-small-web-layout';
import { useOrderInboundsStore } from '@/stores/order-inbounds-store';
import { useToasts } from '@/contexts/toast-context';
import { useUpdateAssignedEmployee } from '@/screens/order-inbounds/queries';

export const ORDER_INBOUND_LIST_ITEM_HEIGHT = 72;
export const ORDER_INBOUND_LIST_ITEM_TIME_WIDTH = 80;
export const ORDER_INBOUND_LIST_ITEM_MARGIN = 16;

const isSubmissionMode = (option: OrderInboundOption) =>
  option.metadata?.workflowMode === OrderAgentWorkflowMode.Submission;

// Only an explicitly closed (correction-mode) order is dimmed — an option
// without metadata is not yet known to be closed.
const isCorrectionMode = (option: OrderInboundOption) =>
  option.metadata?.workflowMode === OrderAgentWorkflowMode.Correction;

interface ActiveSelection {
  mergeUUID: string | null;
  order: OrderInboundOption | null;
  rejectedUpdatedAt: string | null;
  submittedOrderUUID: string | null;
  threadIds: string[];
}

export interface Props {
  activeSelection: ActiveSelection;
  collapseHeaderOnScroll?: boolean;
  getRemoveHandler: (
    option: OrderInboundOption,
    onCloseConfirmationModal?: () => void,
  ) => () => void;
  hasLoadFailed?: boolean;
  isFetchingNextPage?: boolean;
  isSmallMobile: boolean;
  isViewOnly?: boolean;
  ListHeaderComponent?: ReactNode;
  listRef: RefObject<RecyclerListRef<OrderInboundOption> | null>;
  ListStickyHeaderComponent?: ReactNode;
  navigateToOrderDetail: (option: OrderInboundOption) => void;
  onEndReached?: () => void;
  onRetry?: () => void;
  ordersByCustomer: OrdersByCustomerMap;
  refetch: () => void;
  renderFullWidth: boolean;
  scrollViewProps?: ScrollViewProps;
  sectionData: OrderInboundSection[] | null;
  setActiveCustomerId: (id: string, orderUUID?: string) => void;
  setActiveSelection: (selection: ActiveSelection) => void;
}

export const OrderInboundsOrderList: FC<Props> = ({
  activeSelection,
  collapseHeaderOnScroll = false,
  getRemoveHandler,
  hasLoadFailed,
  isFetchingNextPage,
  isSmallMobile,
  isViewOnly,
  ListHeaderComponent,
  ListStickyHeaderComponent,
  listRef,
  navigateToOrderDetail,
  onEndReached,
  onRetry,
  ordersByCustomer,
  refetch,
  renderFullWidth,
  scrollViewProps,
  sectionData,
  setActiveCustomerId,
  setActiveSelection,
}) => {
  const [styles, theme] = useStyles();
  const [sharedStyles] = useSharedOrderListStyles();
  const activeInboundListFilter = useOrderInboundsStore(
    (store) => store.activeStatusFilter,
  );
  const { user } = useAuthenticatedUserContext();
  const { showErrorToast } = useToasts();
  const updateAssignedEmployee = useUpdateAssignedEmployee();

  const { animatedStyle: headerAnimatedStyle, handleScroll } =
    useHideOnScrollAnimation({
      isEnabled: collapseHeaderOnScroll,
      onScroll: scrollViewProps?.onScroll,
    });

  const listScrollViewProps = useMemo<ScrollViewProps>(
    () => ({
      ...scrollViewProps,
      onScroll: handleScroll,
      scrollEventThrottle: scrollViewProps?.scrollEventThrottle ?? 16,
    }),
    [scrollViewProps, handleScroll],
  );

  const swipeableItemRef = useRef<SwipeableWithInstantClose>(null);
  // Keeps a row's right actions mounted while its assign menu is open, even
  // after the pointer leaves the row on its way into the menu -- otherwise
  // losing hover unmounts the menu before a selection can be made.
  const [openAssignMenuOrderValue, setOpenAssignMenuOrderValue] = useState<
    string | null
  >(null);

  const onAssignEmployee = useCallback(
    async (option: OrderInboundOption, employeeUUID: string = user.uuid) => {
      try {
        const smartOrderUUIDs = option.metadata?.orderUUIDs ?? [
          String(option.value),
        ];
        await updateAssignedEmployee({
          employeeUUID,
          smartOrderUUIDs,
          supplierUUID: option.metadata?.supplierUUID ?? '',
        });
      } catch (err) {
        showErrorToast();
      }
    },
    [updateAssignedEmployee, user.uuid, showErrorToast],
  );

  const onUnassignEmployee = useCallback(
    async (option: OrderInboundOption) => {
      try {
        const smartOrderUUIDs = option.metadata?.orderUUIDs ?? [
          String(option.value),
        ];
        await updateAssignedEmployee({
          employeeUUID: null,
          smartOrderUUIDs,
          supplierUUID: option.metadata?.supplierUUID ?? '',
        });
      } catch (err) {
        showErrorToast();
      }
    },
    [updateAssignedEmployee, showErrorToast],
  );

  const onMergeOrder = useCallback(
    (option: OrderInboundOption) => {
      const customerKey = option.metadata?.restaurantUUID ?? option.value;
      const ordersForCustomer = ordersByCustomer[String(customerKey)];

      if (!ordersForCustomer) {
        showErrorToast();
        Analytics.track('order_inbounds_list_screen.merge_orders_error', {
          metadata: option.metadata,
          restaurantUUID: option.metadata?.restaurantUUID,
        });
        return;
      }

      Analytics.track('order_inbounds_list_screen.merge_button_pressed', {
        customerKey: String(customerKey),
        orderUUID: option.value,
      });

      setActiveCustomerId(String(customerKey), String(option.value));
    },
    [ordersByCustomer, setActiveCustomerId, showErrorToast],
  );

  const closeSwipeable = useCallback(() => {
    swipeableItemRef.current?.close();
    swipeableItemRef.current = null;
  }, [swipeableItemRef]);

  const getCanMergeOrder = useCallback(
    (option: OrderInboundOption) =>
      Boolean(option.metadata?.restaurantUUID) &&
      ordersByCustomer[option.metadata!.restaurantUUID!].data.length > 1,
    [ordersByCustomer],
  );

  const isOptionViewOnly = useCallback(
    (option: OrderInboundOption) => isViewOnly || !isSubmissionMode(option),
    [isViewOnly],
  );

  const isSmallWebLayout = useIsSmallWebLayout();

  const listItemProps: Partial<ListItemProps<OrderInboundOption>> =
    useMemo(() => {
      const props: Partial<ListItemProps<OrderInboundOption>> = {
        leftComponentStyle: styles.leftComponentStyle,
        renderLeftComponent: (option, isHovered) => (
          <View style={styles.leftIcon}>
            <OrderInboundsListItemSourceIcon
              assignedEmployee={option.metadata?.assignedEmployee}
              iconStyle={
                isHovered && !isSmallWebLayout ? styles.leftIconHovered : null
              }
              isDisabled={isCorrectionMode(option)}
              isMerged={Boolean(option.metadata?.mergeUUID)}
              isProcessing={Boolean(option.metadata?.isProcessing)}
              source={option.metadata?.source}
            />
          </View>
        ),
        renderRightComponent: (option, isHovered) => {
          const optionValue = String(option.value);
          const isAssignMenuOpen = openAssignMenuOrderValue === optionValue;
          return Platform.OS !== 'web' ||
            isOptionViewOnly(option) ||
            isSmallWebLayout ? null : !isHovered && !isAssignMenuOpen ? null : (
            <OrderInboundsOrderListItemRightActions
              assignedEmployee={option.metadata?.assignedEmployee}
              canMerge={getCanMergeOrder(option)}
              isHovered={isHovered ?? false}
              isSelected={option.value === activeSelection.order?.value}
              onAssignEmployee={(employeeUUID) =>
                onAssignEmployee(option, employeeUUID)
              }
              onAssignMenuOpenChange={(isOpen) =>
                setOpenAssignMenuOrderValue(isOpen ? optionValue : null)
              }
              onMerge={() => onMergeOrder(option)}
              onRemove={getRemoveHandler(option)}
              onUnassignEmployee={() => onUnassignEmployee(option)}
              renderFullWidth={renderFullWidth}
              restaurantName={option.metadata?.restaurantName}
              smartOrderUUID={optionValue}
              supplierUUID={option.metadata?.supplierUUID ?? ''}
            />
          );
        },
        rightComponentStyle: [
          styles.rightComponentStyle,
          isSmallWebLayout ? styles.rightComponentStyleSmallWeb : null,
        ],
        swipeableProps:
          (Platform.OS === 'web' && !isSmallWebLayout) || isViewOnly
            ? undefined
            : {
                onSwipeableRightWillOpen: (_, swipeableRef) => {
                  if (
                    swipeableItemRef.current &&
                    swipeableItemRef.current !== swipeableRef
                  ) {
                    closeSwipeable();
                  }
                  swipeableItemRef.current = swipeableRef;
                },
                renderRightActions: (option) => {
                  if (isOptionViewOnly(option)) {
                    return null;
                  }

                  const shouldShowMerge =
                    Boolean(option.metadata?.restaurantUUID) &&
                    ordersByCustomer[option.metadata!.restaurantUUID!].data
                      .length > 1;

                  return (
                    <OrderInboundsOrderListItemSwipeableActions
                      assignedEmployee={option.metadata?.assignedEmployee}
                      canMerge={shouldShowMerge}
                      onAssignEmployee={() => {
                        onAssignEmployee(option);
                        closeSwipeable();
                      }}
                      onMerge={() => {
                        onMergeOrder(option);
                        closeSwipeable();
                      }}
                      onRemove={getRemoveHandler(option, closeSwipeable)}
                      onUnassignEmployee={() => {
                        onUnassignEmployee(option);
                        closeSwipeable();
                      }}
                      restaurantName={option.metadata?.restaurantName}
                    />
                  );
                },
              },
      };

      props.renderMainContent = (option) => (
        <OrderInboundsOrderListItem option={option} />
      );

      return props;
    }, [
      styles.leftComponentStyle,
      styles.rightComponentStyle,
      styles.rightComponentStyleSmallWeb,
      styles.leftIcon,
      styles.leftIconHovered,
      isSmallWebLayout,
      isViewOnly,
      isOptionViewOnly,
      getCanMergeOrder,
      activeSelection.order?.value,
      getRemoveHandler,
      renderFullWidth,
      onAssignEmployee,
      onMergeOrder,
      onUnassignEmployee,
      closeSwipeable,
      ordersByCustomer,
      openAssignMenuOrderValue,
    ]);

  const isEmpty =
    sectionData?.every((section) => section.data.length === 0) ?? false;

  return (
    <View style={styles.container}>
      <Animated.View
        style={collapseHeaderOnScroll ? headerAnimatedStyle : null}
      >
        {ListHeaderComponent ? (
          ListHeaderComponent
        ) : (
          <InboundListScreenHeader
            containerStyle={
              isSmallMobile ? styles.headerContainerSmallMobile : null
            }
          />
        )}
      </Animated.View>

      {ListStickyHeaderComponent}

      <OrderInboundsList
        isLoading={!sectionData}
        ListEmptyComponent={
          hasLoadFailed && onRetry ? (
            <OrderInboundsOrderListErrorState
              onRetry={onRetry}
              style={styles.emptyState}
            />
          ) : isEmpty ? (
            <OrderInboundsOrderListEmptyState
              filter={activeInboundListFilter}
              style={styles.emptyState}
            />
          ) : null
        }
        ListFooterComponent={isFetchingNextPage ? <ActivityIndicator /> : null}
        listItemProps={listItemProps}
        listRef={listRef}
        onEndReached={onEndReached}
        onRefresh={refetch}
        onSelect={(option) => {
          if (option.metadata?.orderUUIDs) {
            setActiveSelection({
              mergeUUID: option.metadata?.mergeUUID ?? null,
              order: option,
              rejectedUpdatedAt: option.metadata?.rejectedUpdatedAt ?? null,
              submittedOrderUUID: option.metadata?.submittedOrderUUID ?? null,
              threadIds: option.metadata?.threadIds ?? [],
            });
            if (renderFullWidth) {
              navigateToOrderDetail(option);
            }
          }
        }}
        renderSectionHeader={(item) => {
          const { isFirstSection, canMerge } = item.metadata ?? {};
          const headerStyle = [
            sharedStyles.sectionHeader,
            isFirstSection ? sharedStyles.sectionHeaderFirst : null,
          ];
          const mergeButtonStyles = [
            styles.headerButton,
            !canMerge ? styles.mergeButtonDisabled : null,
          ];

          return (
            <View
              key={item.title}
              style={[
                headerStyle,
                isFirstSection ? null : styles.sectionDivider,
              ]}
            >
              <Text style={[FONTS.primarySemiBold, sharedStyles.subTitle]}>
                {item.title}
              </Text>
              {!canMerge || !item.metadata || isViewOnly ? null : (
                <TouchableOpacity
                  hitSlop={ICON_BUTTON_HIT_SLOP}
                  onPress={() => {
                    // TODO: after https://github.com/usepepper/restaurant-app/pull/2779 lands,
                    // add customer key to metadata for consistency
                    const customerKey =
                      item.metadata?.restaurantUUID ?? item.title;

                    const ordersForCustomer = ordersByCustomer[customerKey];

                    if (!ordersForCustomer) {
                      showErrorToast();
                      Analytics.track(
                        'order_inbounds_list_screen.merge_orders_error',
                        {
                          metadata: item.metadata,
                          restaurantUUID: item.metadata?.restaurantUUID,
                        },
                      );
                      return;
                    }

                    setActiveCustomerId(customerKey);
                  }}
                  style={mergeButtonStyles}
                  testID="order-inbounds-merge-orders-icon"
                >
                  <MergeIcon fill={theme.sk.color.icon.primary} />
                </TouchableOpacity>
              )}
            </View>
          );
        }}
        scrollViewProps={listScrollViewProps}
        sections={sectionData || skeletonData}
        selectedOption={activeSelection.order}
      />
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  container: {
    flex: 1,
  },
  emptyState: {
    marginTop: 44,
  },
  headerButton: {
    padding: 8,
  },
  headerContainerSmallMobile: {
    marginTop: PAGE_CONTAINER_HEADER_HEIGHT,
  },
  leftComponentStyle: {
    marginRight: 0,
  },
  leftIcon: {
    marginRight: LIST_MARGIN_HORIZONTAL,
  },
  leftIconHovered: {
    backgroundColor: theme.sk.color.bg.secondary.hover,
  },
  mergeButtonDisabled: {
    opacity: 0.5,
  },
  rightComponentStyle: {
    alignSelf: 'flex-start',
    marginLeft: 0,
    position: 'static',
  },
  rightComponentStyleSmallWeb: {
    alignSelf: 'center',
    marginLeft: ORDER_INBOUND_LIST_ITEM_MARGIN,
  },
  sectionDivider: {
    borderTopColor: theme.colors.divider,
    borderTopStyle: 'solid',
    borderTopWidth: 1,
  },
}));
